import React, { useState, useMemo } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Info, 
  ChevronDown, 
  ChevronUp,
  Award,
  Building2,
  Users,
  BookOpen,
  ExternalLink,
  AlertCircle,
  Target,
  Zap,
  ArrowRight,
  Sparkles,
  Map
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { formatCurrency } from '../lib/utils';
import { useApp } from '../context/AppContext';
import {
  INDUSTRY_TYPES,
  COMPANY_SIZES,
  RESEARCH_SOURCES,
  LEI_TIERS,
  PERCENTILE_PRESETS,
  getBenchmarks,
  calculatePercentile,
  getPerformanceTier,
  getLeiTier,
  getValueForPercentile,
  calculateRequiredLei,
} from '../lib/benchmarks';
import InterventionRoadmap from './InterventionRoadmap';

// Explore Card component for better visual hierarchy
const ExploreCard = ({ 
  icon: Icon, 
  title, 
  description, 
  ctaText, 
  isOpen, 
  onToggle, 
  colorScheme = 'purple',
  badge = null,
  testId,
  children 
}) => {
  const colorClasses = {
    purple: {
      border: isOpen ? 'border-purple-300 bg-purple-50' : 'border-slate-200 hover:border-purple-200 hover:bg-purple-50/50',
      icon: isOpen ? 'bg-purple-200' : 'bg-purple-100 group-hover:bg-purple-200',
      iconColor: 'text-purple-600',
      chevron: isOpen ? 'text-purple-500' : 'text-slate-400',
      cta: 'text-purple-600',
      content: 'border-purple-200'
    },
    teal: {
      border: isOpen ? 'border-teal-300 bg-teal-50' : 'border-slate-200 hover:border-teal-200 hover:bg-teal-50/50',
      icon: isOpen ? 'bg-teal-200' : 'bg-teal-100 group-hover:bg-teal-200',
      iconColor: 'text-teal-600',
      chevron: isOpen ? 'text-teal-500' : 'text-slate-400',
      cta: 'text-teal-600',
      content: 'border-teal-200'
    },
    blue: {
      border: isOpen ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-blue-200 hover:bg-blue-50/50',
      icon: isOpen ? 'bg-blue-200' : 'bg-blue-100 group-hover:bg-blue-200',
      iconColor: 'text-blue-600',
      chevron: isOpen ? 'text-blue-500' : 'text-slate-400',
      cta: 'text-blue-600',
      content: 'border-blue-200'
    },
    amber: {
      border: isOpen ? 'border-amber-300 bg-amber-50' : 'border-slate-200 hover:border-amber-200 hover:bg-amber-50/50',
      icon: isOpen ? 'bg-amber-200' : 'bg-amber-100 group-hover:bg-amber-200',
      iconColor: 'text-amber-600',
      chevron: isOpen ? 'text-amber-500' : 'text-slate-400',
      cta: 'text-amber-600',
      content: 'border-amber-200'
    }
  };

  const colors = colorClasses[colorScheme];

  return (
    <div className="group">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <button 
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${colors.border}`}
            data-testid={testId}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${colors.icon} transition-colors`}>
                <Icon className={`h-5 w-5 ${colors.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-slate-800">{title}</h4>
                    {badge}
                  </div>
                  {isOpen ? (
                    <ChevronUp className={`h-4 w-4 ${colors.chevron} flex-shrink-0`} />
                  ) : (
                    <ChevronDown className={`h-4 w-4 ${colors.chevron} flex-shrink-0`} />
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {description}
                </p>
                {!isOpen && (
                  <span className={`inline-flex items-center gap-1 text-xs ${colors.cta} mt-2 font-medium`}>
                    {ctaText} <ArrowRight className="h-3 w-3" />
                  </span>
                )}
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className={`mt-3 p-4 bg-white rounded-xl border ${colors.content} shadow-sm`}>
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

// Percentile gauge component
const PercentileGauge = ({ percentile, label, value, format = 'default', currency = 'USD' }) => {
  const tier = getPerformanceTier(percentile);
  
  const formatValue = (val) => {
    switch (format) {
      case 'roi': return `${(val / 100).toFixed(1)}x`;
      case 'currency': return formatCurrency(Math.round(val), currency);
      case 'percent': return `${val.toFixed(1)}%`;
      case 'months': return `${val.toFixed(1)} mo`;
      default: return val;
    }
  };
  
  const tierColors = {
    emerald: { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-700' },
    teal: { bg: 'bg-teal-500', light: 'bg-teal-100', text: 'text-teal-700' },
    blue: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700' },
    amber: { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-700' },
    red: { bg: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-700' },
  };
  
  const colors = tierColors[tier.color] || tierColors.blue;
  
  return (
    <div className="p-4 border rounded-lg bg-white">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-600">{label}</span>
        <Badge variant="outline" className={`text-[10px] ${colors.light} ${colors.text} border-0`}>
          {tier.label}
        </Badge>
      </div>
      <div className="text-2xl font-bold text-slate-900 mb-2">
        {formatValue(value)}
      </div>
      {/* Gauge bar */}
      <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`absolute left-0 top-0 h-full ${colors.bg} rounded-full transition-all duration-500`}
          style={{ width: `${percentile}%` }}
        />
        {/* Percentile markers */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-slate-300" />
        <div className="absolute top-0 left-1/2 w-px h-full bg-slate-400" />
        <div className="absolute top-0 left-3/4 w-px h-full bg-slate-300" />
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-slate-400">
        <span>25th</span>
        <span>50th</span>
        <span>75th</span>
        <span>90th</span>
      </div>
      <p className="text-xs text-center mt-2 text-slate-500">
        <span className="font-semibold">{percentile}th</span> percentile
      </p>
    </div>
  );
};

// Benchmark range display
const BenchmarkRange = ({ benchmarkData, currentValue, format = 'roi', currency = 'USD' }) => {
  const formatVal = (val) => {
    switch (format) {
      case 'roi': return `${(val / 100).toFixed(1)}x`;
      case 'currency': return formatCurrency(Math.round(val), currency);
      case 'percent': return `${val.toFixed(1)}%`;
      case 'months': return `${val.toFixed(0)}mo`;
      default: return val;
    }
  };
  
  const { p25, p50, p75, p90 } = benchmarkData;
  
  // Handle negative values by expanding the range
  const minVal = Math.min(0, currentValue, p25) * 1.1;
  const maxVal = Math.max(p90, currentValue) * 1.1;
  const range = maxVal - minVal;
  
  // Calculate position as percentage of the total range
  const getPosition = (val) => {
    if (range === 0) return 50;
    const pos = ((val - minVal) / range) * 100;
    return Math.max(0, Math.min(100, pos));
  };
  
  // Check if we need to show negative region
  const hasNegative = minVal < 0;
  const zeroPosition = hasNegative ? getPosition(0) : 0;
  
  return (
    <div className="mt-3">
      <div className="relative h-8 rounded overflow-hidden">
        {/* Background gradient - split for negative values */}
        {hasNegative ? (
          <>
            {/* Negative region (red) */}
            <div 
              className="absolute top-0 h-full bg-gradient-to-r from-rose-200 to-rose-100"
              style={{ left: 0, width: `${zeroPosition}%` }}
            />
            {/* Positive region (normal gradient) */}
            <div 
              className="absolute top-0 h-full bg-gradient-to-r from-amber-100 via-blue-100 via-teal-100 to-emerald-100"
              style={{ left: `${zeroPosition}%`, width: `${100 - zeroPosition}%` }}
            />
            {/* Zero line */}
            <div 
              className="absolute top-0 h-full border-l-2 border-slate-500"
              style={{ left: `${zeroPosition}%` }}
            >
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] text-slate-600 font-medium">0x</span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-rose-100 via-amber-100 via-blue-100 via-teal-100 to-emerald-100" />
        )}
        
        {/* Range markers */}
        <div className="absolute top-0 h-full border-l border-slate-300" style={{ left: `${getPosition(p25)}%` }}>
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] text-slate-400">P25</span>
        </div>
        <div className="absolute top-0 h-full border-l-2 border-slate-400" style={{ left: `${getPosition(p50)}%` }}>
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] text-slate-500 font-medium">Median</span>
        </div>
        <div className="absolute top-0 h-full border-l border-slate-300" style={{ left: `${getPosition(p75)}%` }}>
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] text-slate-400">P75</span>
        </div>
        <div className="absolute top-0 h-full border-l border-slate-300" style={{ left: `${getPosition(p90)}%` }}>
          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] text-slate-400">P90</span>
        </div>
        
        {/* Current value marker */}
        <div 
          className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-lg ${
            currentValue < 0 ? 'bg-rose-500' : 'bg-purple-600'
          }`}
          style={{ left: `calc(${getPosition(currentValue)}% - 8px)` }}
        >
          <span className={`absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold whitespace-nowrap ${
            currentValue < 0 ? 'text-rose-600' : 'text-purple-600'
          }`}>
            You: {formatVal(currentValue)}
          </span>
        </div>
      </div>
      <div className="flex justify-between mt-6 text-[9px] text-slate-500">
        {hasNegative && <span className="text-rose-500">{formatVal(minVal / 1.1)}</span>}
        <span>{formatVal(p25)}</span>
        <span>{formatVal(p50)}</span>
        <span>{formatVal(p75)}</span>
        <span>{formatVal(p90)}</span>
      </div>
    </div>
  );
};

const BenchmarkComparison = ({ results, configuration, measures = [] }) => {
  const [selectedIndustry, setSelectedIndustry] = useState('technology');
  const [selectedSize, setSelectedSize] = useState('midmarket');
  const [showMethodology, setShowMethodology] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const [showGoalSetting, setShowGoalSetting] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [isBenchmarkExpanded, setIsBenchmarkExpanded] = useState(false);  // NEW: Collapsed by default
  
  // Get currency from context
  const { currency } = useApp();
  
  // Goal setting state
  const [goalMode, setGoalMode] = useState('preset'); // 'preset' or 'custom'
  const [roiGoalPercentile, setRoiGoalPercentile] = useState(75);
  const [kpiGoalPercentile, setKpiGoalPercentile] = useState(75);
  const [paybackGoalPercentile, setPaybackGoalPercentile] = useState(75);
  
  // Get current metrics
  const currentRoi = results?.roi_percent || results?.total_roi_percent || 0;
  const currentKpiUplift = results?.kpi_delta_percent || results?.total_kpi_delta_percent || 0;
  const currentPayback = results?.payback_months || 0;
  const leiImprovement = configuration?.lei_improvement || 10;
  
  // Get benchmarks for selected industry/size
  const benchmarks = getBenchmarks(selectedIndustry, selectedSize);
  
  // Calculate percentiles
  const roiPercentile = calculatePercentile(currentRoi, benchmarks.roi);
  const kpiPercentile = calculatePercentile(currentKpiUplift, benchmarks.kpiUplift);
  // Payback is inverse - lower is better
  const paybackPercentile = 100 - calculatePercentile(currentPayback, benchmarks.paybackMonths);
  
  // Get LEI tier info
  const leiTier = getLeiTier(leiImprovement);
  
  // Get industry and size labels
  const industryLabel = INDUSTRY_TYPES.find(i => i.id === selectedIndustry)?.label || 'Technology';
  const sizeLabel = COMPANY_SIZES.find(s => s.id === selectedSize)?.label || 'Mid-Market';
  
  // Overall score (weighted average)
  const overallPercentile = Math.round((roiPercentile * 0.4 + kpiPercentile * 0.3 + paybackPercentile * 0.3));
  const overallTier = getPerformanceTier(overallPercentile);

  // Goal calculations
  const goalCalculations = useMemo(() => {
    // Target values based on goal percentiles
    const targetRoi = getValueForPercentile(roiGoalPercentile, benchmarks.roi);
    const targetKpi = getValueForPercentile(kpiGoalPercentile, benchmarks.kpiUplift);
    const targetPayback = getValueForPercentile(100 - paybackGoalPercentile, benchmarks.paybackMonths); // Inverse for payback
    
    // Calculate required LEI for each goal
    const requiredLeiForRoi = calculateRequiredLei(leiImprovement, currentRoi, targetRoi);
    const requiredLeiForKpi = calculateRequiredLei(leiImprovement, currentKpiUplift, targetKpi);
    // For payback, lower is better, so inverse relationship
    const requiredLeiForPayback = currentPayback > 0 && targetPayback > 0 
      ? calculateRequiredLei(leiImprovement, 1/currentPayback, 1/targetPayback)
      : leiImprovement;
    
    // Maximum required LEI across all goals
    const maxRequiredLei = Math.max(requiredLeiForRoi, requiredLeiForKpi, requiredLeiForPayback);
    
    // Gap analysis
    const roiGap = targetRoi - currentRoi;
    const kpiGap = targetKpi - currentKpiUplift;
    const paybackGap = currentPayback - targetPayback; // Positive means improvement needed
    
    return {
      roi: {
        current: currentRoi,
        target: targetRoi,
        gap: roiGap,
        gapPercent: currentRoi > 0 ? (roiGap / currentRoi) * 100 : 0,
        requiredLei: requiredLeiForRoi,
        leiIncrease: requiredLeiForRoi - leiImprovement,
        achieved: roiPercentile >= roiGoalPercentile,
      },
      kpi: {
        current: currentKpiUplift,
        target: targetKpi,
        gap: kpiGap,
        gapPercent: currentKpiUplift > 0 ? (kpiGap / currentKpiUplift) * 100 : 0,
        requiredLei: requiredLeiForKpi,
        leiIncrease: requiredLeiForKpi - leiImprovement,
        achieved: kpiPercentile >= kpiGoalPercentile,
      },
      payback: {
        current: currentPayback,
        target: targetPayback,
        gap: paybackGap,
        gapPercent: currentPayback > 0 ? (paybackGap / currentPayback) * 100 : 0,
        requiredLei: requiredLeiForPayback,
        leiIncrease: requiredLeiForPayback - leiImprovement,
        achieved: paybackPercentile >= paybackGoalPercentile,
      },
      overall: {
        maxRequiredLei,
        leiIncrease: maxRequiredLei - leiImprovement,
        allAchieved: roiPercentile >= roiGoalPercentile && 
                     kpiPercentile >= kpiGoalPercentile && 
                     paybackPercentile >= paybackGoalPercentile,
        currentLei: leiImprovement,
        targetTier: getLeiTier(maxRequiredLei),
      }
    };
  }, [benchmarks, roiGoalPercentile, kpiGoalPercentile, paybackGoalPercentile, 
      currentRoi, currentKpiUplift, currentPayback, leiImprovement, 
      roiPercentile, kpiPercentile, paybackPercentile]);
  
  return (
    <>
    <Card className="border-purple-200 bg-gradient-to-br from-white to-purple-50/30">
      <Collapsible open={isBenchmarkExpanded} onOpenChange={setIsBenchmarkExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-purple-50/50 transition-colors rounded-t-lg">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Award className="h-5 w-5 text-purple-600" />
                <div>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    Industry Benchmark Comparison
                    {isBenchmarkExpanded ? (
                      <ChevronUp className="h-4 w-4 text-purple-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-purple-400" />
                    )}
                  </CardTitle>
                  {!isBenchmarkExpanded && (
                    <p className="text-xs text-slate-500 mt-1">
                      Click to expand and see detailed benchmark comparison
                    </p>
                  )}
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                overallTier.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                overallTier.color === 'teal' ? 'bg-teal-100 text-teal-700' :
                overallTier.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                overallTier.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                'bg-rose-100 text-rose-700'
              }`}>
                <Award className="h-4 w-4" />
                <div className="text-right">
                  <p className="text-xs font-medium">{overallTier.label}</p>
                  <p className="text-lg font-bold">{overallPercentile}th %ile</p>
                </div>
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardHeader className="pt-0">
            <CardDescription className="mt-1">
              See how your results compare to industry standards
            </CardDescription>
            
            {/* Disclaimer banner */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-800">
                <span className="font-medium">Estimated Benchmarks:</span> These benchmarks combine industry research with model-derived estimates. 
                They provide directional guidance, not absolute standards. 
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowMethodology(!showMethodology); }}
                  className="ml-1 underline hover:no-underline"
                >
                  Learn more
                </button>
              </div>
            </div>
          </CardHeader>
      
          <CardContent className="space-y-6">
            {/* Industry & Size Selectors */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Industry
                </label>
                <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                  <SelectTrigger data-testid="benchmark-industry-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_TYPES.map(industry => (
                      <SelectItem key={industry.id} value={industry.id}>
                        <div>
                          <span className="font-medium">{industry.label}</span>
                          <span className="text-xs text-slate-500 ml-2">{industry.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block flex items-center gap-1">
                  <Users className="h-3 w-3" /> Company Size
                </label>
                <Select value={selectedSize} onValueChange={setSelectedSize}>
                  <SelectTrigger data-testid="benchmark-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map(size => (
                      <SelectItem key={size.id} value={size.id}>
                        <div>
                          <span className="font-medium">{size.label}</span>
                          <span className="text-xs text-slate-500 ml-2">({size.range})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              Comparing against <span className="font-medium text-slate-700">{industryLabel}</span> companies of <span className="font-medium text-slate-700">{sizeLabel}</span> size
            </p>
            
            {/* LEI Tier Card */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-purple-600" />
                  <span className="font-medium text-slate-700">Your LEI Improvement Tier</span>
                </div>
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                  {leiTier.label}: {leiImprovement}%
                </Badge>
              </div>
              <p className="text-sm text-slate-600">{leiTier.description}</p>
              <div className="mt-3 flex items-center gap-4 text-xs">
                <span className="text-slate-500">Expected ROI Range:</span>
                <span className="font-semibold text-purple-700">{leiTier.expectedRoi}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {leiTier.typicalScenarios.map((scenario, idx) => (
                  <Badge key={`scenario-${scenario.slice(0, 20)}-${idx}`} variant="outline" className="text-[9px] bg-white">
                    {scenario}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Percentile Gauges */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PercentileGauge 
                percentile={roiPercentile}
                label="ROI"
                value={currentRoi}
                format="roi"
              />
              <PercentileGauge 
                percentile={kpiPercentile}
                label="KPI Uplift"
                value={currentKpiUplift}
                format="percent"
              />
              <PercentileGauge 
                percentile={paybackPercentile}
                label="Payback Speed"
                value={currentPayback}
                format="months"
              />
            </div>
            
            {/* Detailed Range Comparison */}
            <div className="space-y-6 pt-4 border-t">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Detailed Benchmark Ranges
              </h4>
              
              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-teal-600" />
                    <span className="text-sm font-medium">ROI Distribution</span>
                  </div>
                  <BenchmarkRange 
                    benchmarkData={benchmarks.roi}
                    currentValue={currentRoi}
                    format="roi"
                  />
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium">KPI Uplift Distribution</span>
                  </div>
                  <BenchmarkRange 
                    benchmarkData={benchmarks.kpiUplift}
                    currentValue={currentKpiUplift}
                    format="percent"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>

    {/* ===== IMPROVE YOUR RESULTS SECTION (Standalone) ===== */}
    <div className="mt-8 p-6 bg-gradient-to-br from-purple-50/50 to-white rounded-xl border border-purple-100">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-purple-100 rounded-lg">
          <TrendingUp className="h-6 w-6 text-purple-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">Improve Your Results</h2>
      </div>
      <p className="text-slate-600 mb-6 max-w-3xl">
        Take action to close performance gaps. Set targets, build a roadmap, and identify the L&D interventions that will have the greatest impact on your business KPIs.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Goal Setting & Planning Card */}
        <ExploreCard
          icon={Target}
          title="Goal Setting & Planning"
          description="Set realistic targets based on your baseline and see what LEI improvements are needed to reach your goals."
          ctaText="Plan improvements"
          isOpen={showGoalSetting}
          onToggle={setShowGoalSetting}
          colorScheme="purple"
          badge={goalCalculations.overall.allAchieved && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">All Goals Met!</Badge>
          )}
          testId="show-goal-setting"
        >
          <GoalSettingContent 
            goalMode={goalMode}
            setGoalMode={setGoalMode}
            roiGoalPercentile={roiGoalPercentile}
            setRoiGoalPercentile={setRoiGoalPercentile}
            kpiGoalPercentile={kpiGoalPercentile}
            setKpiGoalPercentile={setKpiGoalPercentile}
            paybackGoalPercentile={paybackGoalPercentile}
            setPaybackGoalPercentile={setPaybackGoalPercentile}
            roiPercentile={roiPercentile}
            kpiPercentile={kpiPercentile}
            paybackPercentile={paybackPercentile}
            goalCalculations={goalCalculations}
            leiImprovement={leiImprovement}
            leiTier={leiTier}
          />
        </ExploreCard>

        {/* Intervention Roadmap Card */}
        <ExploreCard
          icon={Map}
          title="Intervention Roadmap"
          description="See which L&D actions will drive the biggest impact on your KPIs and prioritize your investments."
          ctaText="View roadmap"
          isOpen={showRoadmap}
          onToggle={setShowRoadmap}
          colorScheme="teal"
          badge={measures.length > 0 && (
            <Badge variant="secondary" className="text-[10px] bg-teal-100 text-teal-700">
              {measures.length} measures
            </Badge>
          )}
          testId="show-roadmap"
        >
          <InterventionRoadmap
            measures={measures}
            goalCalculations={goalCalculations}
            benchmarks={benchmarks}
            currentLei={leiImprovement}
            roiGoalPercentile={roiGoalPercentile}
            kpiGoalPercentile={kpiGoalPercentile}
            paybackGoalPercentile={paybackGoalPercentile}
          />
        </ExploreCard>
      </div>
    </div>

    {/* ===== UNDERSTAND YOUR RESULTS SECTION (Standalone) ===== */}
    <div className="mt-6 p-6 bg-gradient-to-br from-blue-50/50 to-white rounded-xl border border-blue-100">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <BookOpen className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">Understand Your Results</h2>
      </div>
      <p className="text-slate-600 mb-6 max-w-3xl">
        Learn how your results compare to industry benchmarks and explore the research behind our calculations. Understanding the methodology helps you communicate findings with confidence.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Benchmark Methodology Card */}
        <ExploreCard
          icon={BarChart3}
          title="Benchmark Methodology"
          description="Understand how benchmarks are calculated and what percentile rankings mean for your organization."
          ctaText="Learn methodology"
          isOpen={showMethodology}
          onToggle={setShowMethodology}
          colorScheme="blue"
          testId="show-methodology"
        >
          <MethodologyContent />
        </ExploreCard>

        {/* Research Sources Card */}
        <ExploreCard
          icon={Info}
          title="Research Sources & Citations"
          description="Review the academic research and industry sources that inform our calculations and benchmarks."
          ctaText="View sources"
          isOpen={showSources}
          onToggle={setShowSources}
          colorScheme="amber"
          testId="show-sources"
        >
          <ResearchSourcesContent sources={RESEARCH_SOURCES} />
        </ExploreCard>
      </div>
    </div>
  </>
  );
};

// Goal Setting Content Component
const GoalSettingContent = ({
  goalMode, setGoalMode,
  roiGoalPercentile, setRoiGoalPercentile,
  kpiGoalPercentile, setKpiGoalPercentile,
  paybackGoalPercentile, setPaybackGoalPercentile,
  roiPercentile, kpiPercentile, paybackPercentile,
  goalCalculations, leiImprovement, leiTier
}) => (
  <div className="space-y-5">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-slate-700">Set Target Percentiles:</span>
      <div className="flex gap-2">
        <Button
          variant={goalMode === 'preset' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setGoalMode('preset')}
          className={goalMode === 'preset' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          Presets
        </Button>
        <Button
          variant={goalMode === 'custom' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setGoalMode('custom')}
          className={goalMode === 'custom' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          Custom
        </Button>
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* ROI Goal */}
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-4 w-4 text-teal-600" />
          <span className="font-medium text-slate-700 text-sm">ROI Goal</span>
          {goalCalculations.roi.achieved && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Achieved!</Badge>
          )}
        </div>
        {goalMode === 'preset' ? (
          <div className="flex flex-wrap gap-1">
            {PERCENTILE_PRESETS.map(preset => (
              <button
                key={preset.value}
                onClick={() => setRoiGoalPercentile(preset.value)}
                className={`px-2 py-1 text-xs rounded-full transition-all ${
                  roiGoalPercentile === preset.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <Slider
              value={[roiGoalPercentile]}
              onValueChange={([v]) => setRoiGoalPercentile(v)}
              min={25} max={99} step={1}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>25th</span>
              <span className="font-medium text-purple-600">{roiGoalPercentile}th</span>
              <span>99th</span>
            </div>
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-2">
          Current: {roiPercentile}th → Target: {roiGoalPercentile}th
        </p>
      </div>

      {/* KPI Goal */}
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="h-4 w-4 text-blue-600" />
          <span className="font-medium text-slate-700 text-sm">KPI Goal</span>
          {goalCalculations.kpi.achieved && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Achieved!</Badge>
          )}
        </div>
        {goalMode === 'preset' ? (
          <div className="flex flex-wrap gap-1">
            {PERCENTILE_PRESETS.map(preset => (
              <button
                key={preset.value}
                onClick={() => setKpiGoalPercentile(preset.value)}
                className={`px-2 py-1 text-xs rounded-full transition-all ${
                  kpiGoalPercentile === preset.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <Slider
              value={[kpiGoalPercentile]}
              onValueChange={([v]) => setKpiGoalPercentile(v)}
              min={25} max={99} step={1}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>25th</span>
              <span className="font-medium text-purple-600">{kpiGoalPercentile}th</span>
              <span>99th</span>
            </div>
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-2">
          Current: {kpiPercentile}th → Target: {kpiGoalPercentile}th
        </p>
      </div>

      {/* Payback Goal */}
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-orange-600" />
          <span className="font-medium text-slate-700 text-sm">Payback Goal</span>
          {goalCalculations.payback.achieved && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Achieved!</Badge>
          )}
        </div>
        {goalMode === 'preset' ? (
          <div className="flex flex-wrap gap-1">
            {PERCENTILE_PRESETS.map(preset => (
              <button
                key={preset.value}
                onClick={() => setPaybackGoalPercentile(preset.value)}
                className={`px-2 py-1 text-xs rounded-full transition-all ${
                  paybackGoalPercentile === preset.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            <Slider
              value={[paybackGoalPercentile]}
              onValueChange={([v]) => setPaybackGoalPercentile(v)}
              min={25} max={99} step={1}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>25th</span>
              <span className="font-medium text-purple-600">{paybackGoalPercentile}th</span>
              <span>99th</span>
            </div>
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-2">
          Current: {paybackPercentile}th → Target: {paybackGoalPercentile}th
        </p>
      </div>
    </div>

    {/* Gap Analysis Summary */}
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-purple-600" />
        <h4 className="font-semibold text-slate-800">Action Plan Summary</h4>
      </div>
      
      {goalCalculations.overall.allAchieved ? (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-emerald-800 font-medium flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Congratulations! You've achieved all benchmark goals.
          </p>
          <p className="text-emerald-700 text-sm mt-1">
            Consider setting more ambitious targets.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <div>
              <span className="text-sm font-medium text-slate-700">Required LEI Improvement</span>
              <p className="text-xs text-slate-500">To reach highest target</p>
            </div>
            <div className="text-right">
              <span className="text-xl font-bold text-purple-600">
                {goalCalculations.overall.maxRequiredLei.toFixed(1)}%
              </span>
              <p className="text-xs text-slate-500">Current: {leiImprovement}%</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className={`p-3 rounded-lg border ${goalCalculations.roi.achieved ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-slate-700 text-sm">ROI</span>
                {!goalCalculations.roi.achieved && goalCalculations.roi.gap > 0 && (
                  <span className="text-xs text-amber-600">+{(goalCalculations.roi.gap/100).toFixed(1)}x</span>
                )}
              </div>
              <div className="text-xs text-slate-600">
                {(goalCalculations.roi.current/100).toFixed(1)}x → {(goalCalculations.roi.target/100).toFixed(1)}x
              </div>
            </div>

            <div className={`p-3 rounded-lg border ${goalCalculations.kpi.achieved ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-slate-700 text-sm">KPI</span>
                {!goalCalculations.kpi.achieved && goalCalculations.kpi.gap > 0 && (
                  <span className="text-xs text-amber-600">+{goalCalculations.kpi.gap.toFixed(1)}%</span>
                )}
              </div>
              <div className="text-xs text-slate-600">
                {goalCalculations.kpi.current.toFixed(1)}% → {goalCalculations.kpi.target.toFixed(1)}%
              </div>
            </div>

            <div className={`p-3 rounded-lg border ${goalCalculations.payback.achieved ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-slate-700 text-sm">Payback</span>
                {!goalCalculations.payback.achieved && goalCalculations.payback.gap > 0 && (
                  <span className="text-xs text-amber-600">-{goalCalculations.payback.gap.toFixed(1)}mo</span>
                )}
              </div>
              <div className="text-xs text-slate-600">
                {goalCalculations.payback.current.toFixed(1)}mo → {goalCalculations.payback.target.toFixed(1)}mo
              </div>
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              <strong>Recommendation:</strong> Move from <span className="font-semibold">{leiTier.label}</span> tier 
              to <span className="font-semibold">{goalCalculations.overall.targetTier.label}</span> tier 
              ({goalCalculations.overall.maxRequiredLei.toFixed(0)}% LEI).
            </p>
          </div>
        </div>
      )}
    </div>
  </div>
);

// Methodology Content Component
const MethodologyContent = () => (
  <div className="space-y-4 text-sm">
    <div>
      <h5 className="font-medium text-slate-700 mb-2">How Benchmarks Are Derived</h5>
      <p className="text-slate-600">Our benchmarks combine three complementary approaches:</p>
      <ol className="list-decimal list-inside mt-2 space-y-2 text-slate-600">
        <li><strong>Research-Backed Data:</strong> Aggregated findings from major L&D industry reports.</li>
        <li><strong>Model-Derived Estimates:</strong> Calculated ranges based on our learning elasticity model.</li>
        <li><strong>Tiered Segmentation:</strong> Adjusted by industry type and company size.</li>
      </ol>
    </div>
    <div>
      <h5 className="font-medium text-slate-700 mb-2">Percentile Interpretation</h5>
      <ul className="space-y-1 text-slate-600">
        <li><span className="font-medium text-emerald-600">90th+:</span> Top performer - exceptional</li>
        <li><span className="font-medium text-teal-600">75th-90th:</span> Above average - strong</li>
        <li><span className="font-medium text-blue-600">50th-75th:</span> Average - typical</li>
        <li><span className="font-medium text-amber-600">25th-50th:</span> Below average - improve</li>
        <li><span className="font-medium text-rose-600">Below 25th:</span> Needs attention</li>
      </ul>
    </div>
    <div className="p-3 bg-blue-50 rounded border border-blue-100">
      <p className="text-blue-800 text-xs">
        <strong>Note:</strong> We plan to incorporate anonymized data to refine benchmarks over time.
      </p>
    </div>
  </div>
);

// Research Sources Content Component
const ResearchSourcesContent = ({ sources }) => (
  <div className="space-y-3">
    {sources.map(source => (
      <div key={source.id} className="p-3 bg-slate-50 rounded-lg border">
        <div className="flex items-start justify-between">
          <div>
            <h6 className="font-medium text-slate-700 text-sm">{source.title}</h6>
            <p className="text-xs text-slate-500">{source.organization} ({source.year})</p>
          </div>
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:text-amber-700">
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        <p className="text-xs text-slate-600 mt-2 italic">"{source.keyFindings}"</p>
      </div>
    ))}
  </div>
);

export default BenchmarkComparison;
